import { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useLaborRatePresets } from '@/hooks/useLaborRatePresets';
import { getUnitLabel } from '@jobos/shared';

interface LaborRateSelectProps {
  value: number;
  unitKey?: string | null;
  onChange: (rate: number, unitKey?: string) => void;
  disabled?: boolean;
  category?: string;
}

export function LaborRateSelect({ value, unitKey, onChange, disabled = false, category = 'labor' }: LaborRateSelectProps) {
  // Defensive coercion: offline-storage recovery (localStorage backup) and
  // older cached query responses can deliver `value` as a numeric string.
  // Without this `value.toFixed(...)` below would crash the entire editor.
  const numericValue: number =
    typeof value === 'number'
      ? value
      : value != null && value !== ('' as unknown)
        ? Number(value as unknown) || 0
        : 0;
  const [open, setOpen] = useState(false);
  const [customRate, setCustomRate] = useState('');
  const customInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number | undefined>(undefined);
  const { data: allPresets = [] } = useLaborRatePresets();

  const presets = allPresets.filter((p) => p.categoryKey === category);

  // Resolve current label for the trigger/custom-rate display
  const currentAbbr = getUnitLabel({ unitKey });

  const rateOptions = presets.map((p) => {
    const abbr = getUnitLabel({ unitKey: p.unitKey });
    return {
      value: p.id,
      label: `${p.label} — $${p.rate.toFixed(2)}/${abbr}`,
      unitKey: p.unitKey,
    };
  });

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  const matchingPreset = presets.find((p) => p.rate === numericValue);

  function handlePresetSelect(presetId: string) {
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      onChange(preset.rate, preset.unitKey ?? undefined);
      setOpen(false);
      setCustomRate('');
    }
  }

  function handleCustomApply() {
    const parsed = parseFloat(customRate);
    if (!isNaN(parsed) && parsed >= 0) {
      onChange(parsed);
      setOpen(false);
      setCustomRate('');
    }
  }

  return (
    <Popover open={open} onOpenChange={(isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) setCustomRate('');
    }}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-7 text-sm px-2"
          disabled={disabled}
        >
          {numericValue > 0 ? `$${numericValue.toFixed(2)}/${currentAbbr}` : 'Rate'}
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-ignore-deselect
        className="p-0"
        align="start"
        style={{ minWidth: buttonWidth ? `${buttonWidth}px` : undefined }}
      >
        <div className="px-3 py-2">
          <label className="text-xs text-muted-foreground mb-1 block">Custom rate</label>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground">$</span>
            <Input
              ref={customInputRef}
              type="number"
              min="0"
              step="0.01"
              value={customRate}
              onChange={(e) => setCustomRate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCustomApply();
                }
              }}
              placeholder="0.00"
              className="h-7 flex-1 px-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-sm text-muted-foreground">/{currentAbbr}</span>
            <Button
              size="sm"
              variant="secondary"
              className="h-7 px-2 text-xs"
              onClick={handleCustomApply}
              disabled={!customRate || isNaN(parseFloat(customRate))}
            >
              Apply
            </Button>
          </div>
        </div>
        <Command
          filter={(value: string, search: string) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput placeholder="Search rates..." />
          <CommandList>
            <CommandEmpty>No matching rate.</CommandEmpty>
            <CommandGroup>
              {rateOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => handlePresetSelect(option.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      matchingPreset?.id === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
